import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CreditCard, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { api, rp } from "@/lib/api";
import { Input } from "@/components/ui/input";

const loadSnap = (url, clientKey) => new Promise((resolve, reject) => {
  if (window.snap) return resolve(window.snap);
  const el = document.createElement("script");
  el.src = url;
  el.setAttribute("data-client-key", clientKey);
  el.onload = () => resolve(window.snap);
  el.onerror = reject;
  document.body.appendChild(el);
});

const Banner = ({ text }) => (
  <div data-testid="sandbox-banner"
    className="rounded-lg border-2 border-lm-red bg-lm-red/10 px-4 py-3 flex items-start gap-2">
    <AlertTriangle size={18} className="text-lm-red shrink-0 mt-0.5" />
    <span className="text-sm font-semibold text-lm-red">{text}</span>
  </div>
);

const Row = ({ label, value, strong }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-ink-soft">{label}</span>
    <span className={strong ? "font-semibold" : ""}>{value}</span>
  </div>
);

export default function SandboxPay() {
  const [cfg, setCfg] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [form, setForm] = useState({ package_id: "", quantity: 1, rental_date: "", start_time: "14:00", delivery_type: "self_pickup", address: "Test" });
  const [quote, setQuote] = useState(null);
  const [tx, setTx] = useState({ transactions: [] });
  const [busy, setBusy] = useState("");

  const loadTx = () => api.get("/sandbox/transactions").then((r) => setTx(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/sandbox/config").then((r) => setCfg(r.data))
      .catch((e) => toast.error(e?.response?.status === 404 ? "Mode sandbox dimatikan di server" : "Gagal memuat konfigurasi"));
    api.get("/public/packages").then((r) => {
      setPkgs(r.data);
      const d = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
      setForm((f) => ({ ...f, package_id: r.data[0]?.id || "", rental_date: d }));
    });
    loadTx();
  }, []);

  const act = async (key, fn) => {
    setBusy(key);
    try { return await fn(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(""); }
  };
  const getQuote = () => act("quote", async () => {
    const r = await api.post("/sandbox/quote", form);
    setQuote(r.data);
    toast.success("Perhitungan harga siap");
  });
  const createAndPay = () => act("pay", async () => {
    const b = (await api.post("/sandbox/bookings", form)).data;
    const p = (await api.post(`/sandbox/pay/${b.booking_code}`)).data;
    const snap = await loadSnap(p.snap_js_url, p.client_key);
    snap.pay(p.snap_token, {
      onSuccess: () => { toast.success("Sandbox: pembayaran sukses"); loadTx(); },
      onPending: () => { toast.info("Sandbox: menunggu pembayaran"); loadTx(); },
      onError: () => { toast.error("Sandbox: pembayaran gagal"); loadTx(); },
      onClose: () => loadTx(),
    });
  });
  const checkStatus = (orderId) => act(orderId, async () => {
    const r = await api.post(`/sandbox/status/${orderId}`);
    toast.success(r.data.found ? `Midtrans: ${r.data.midtrans_status}` : r.data.message);
    loadTx();
  });
  const cleanup = () => {
    if (!window.confirm("Hapus SEMUA data uji sandbox? Data operasional tidak tersentuh.")) return;
    act("cleanup", async () => {
      const r = await api.post("/sandbox/cleanup", { confirm: true });
      toast.success(`Terhapus: ${r.data.removed.bookings} booking uji`);
      loadTx();
    });
  };

  if (!cfg) return <div className="text-ink-soft">Memuat…</div>;

  return (
    <div className="space-y-6" data-testid="admin-sandbox-pay">
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900"
        data-testid="sandbox-mode-label">
        SANDBOX — pembayaran uji coba, bukan uang nyata
      </div>
      {cfg.canonical && (
        <div className={`rounded-lg border px-4 py-3 text-xs ${cfg.canonical.https_ok
          ? "border-black/10 bg-black/[0.02]" : "border-red-300 bg-red-50 text-red-800"}`}
          data-testid="webhook-config-check">
          <div className="text-sm font-semibold">
            {cfg.canonical.https_ok
              ? "URL yang harus diisi di dashboard Midtrans (HTTPS, tanpa garis miring di akhir)"
              : "Peringatan konfigurasi: Notification URL wajib HTTPS"}
          </div>
          <p className="mt-1.5">
            Domain memaksa HTTP → HTTPS dengan kode 301, dan Midtrans menolak redirect 301/302/303.
            Kalau dashboard masih memakai <code>http://</code>, webhook tidak akan pernah masuk.
          </p>
          <ul className="mt-2 space-y-1 font-mono text-[11px] break-all">
            <li data-testid="canonical-notification-url">Payment Notification: {cfg.canonical.notification_url}</li>
            <li>Recurring Notification: {cfg.canonical.recurring_notification_url}</li>
            <li>Pay Account Notification: {cfg.canonical.pay_account_notification_url}</li>
            <li>Finish: {cfg.canonical.finish_url}</li>
            <li>Unfinish: {cfg.canonical.unfinish_url}</li>
            <li>Error: {cfg.canonical.error_url}</li>
          </ul>
          {cfg.canonical.warning && <p className="mt-2 font-semibold">{cfg.canonical.warning}</p>}
          <p className="mt-2">{cfg.canonical.note}</p>
        </div>
      )}
      <div>
        <h1 className="font-display text-3xl tracking-tight">Test Pembayaran Midtrans</h1>
        <p className="mt-2 text-sm text-ink-soft max-w-2xl">
          Halaman khusus owner untuk menguji alur pembayaran Midtrans mode Sandbox. Customer publik
          tidak bisa mengaksesnya dan checkout online di website tetap mati
          ({cfg.public_payment_enabled ? "AKTIF" : "nonaktif"}).
        </p>
      </div>
      <Banner text={cfg.warning} />

      {!cfg.ready && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="sandbox-not-ready">
          <div className="font-semibold text-amber-800">Kunci Sandbox belum diisi</div>
          <p className="mt-1 text-amber-900">{cfg.setup_hint}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="sandbox-form">
          <h2 className="text-sm font-semibold">Buat booking uji</h2>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <label className="text-xs text-ink-soft">Paket
              <select data-testid="sandbox-package" value={form.package_id}
                onChange={(e) => setForm({ ...form, package_id: e.target.value })}
                className="mt-1 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                {pkgs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-ink-soft">Jumlah unit
              <Input data-testid="sandbox-qty" type="number" min={1} max={5} value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} className="mt-1" />
            </label>
            <label className="text-xs text-ink-soft">Tanggal
              <Input data-testid="sandbox-date" type="date" value={form.rental_date}
                onChange={(e) => setForm({ ...form, rental_date: e.target.value })} className="mt-1" />
            </label>
            <label className="text-xs text-ink-soft">Jam mulai
              <Input data-testid="sandbox-time" type="time" value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" />
            </label>
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <button data-testid="sandbox-quote-btn" onClick={getQuote} disabled={busy === "quote"}
              className="h-10 px-4 rounded-md border border-black/15 text-sm">Hitung harga</button>
            <button data-testid="sandbox-pay-btn" onClick={createAndPay} disabled={busy === "pay" || !cfg.ready}
              className="h-10 px-5 rounded-md lm-btn-primary text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
              {busy === "pay" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              Bayar via Midtrans Sandbox
            </button>
          </div>
          {quote && (
            <div className="mt-4 border-t border-black/5 pt-3" data-testid="sandbox-quote">
              <Row label="Sewa" value={rp(quote.rental_subtotal)} />
              <Row label="Add-on" value={rp(quote.addon_total)} />
              <Row label="Antar / ambil" value={rp(quote.delivery_fee + quote.pickup_fee)} />
              <Row label="Diskon" value={`- ${rp(quote.promo_discount + quote.manual_discount + quote.loyalty_discount + quote.pickup_discount)}`} />
              <Row label="Deposit (dikembalikan)" value={rp(quote.deposit_amount)} />
              <Row label="Total bayar" value={rp(quote.total_payment)} strong />
            </div>
          )}
        </section>

        <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="sandbox-cards">
          <h2 className="text-sm font-semibold">Kartu tes resmi Midtrans Sandbox</h2>
          <div className="mt-2 divide-y divide-black/5">
            {cfg.test_cards.map((c) => (
              <div key={c.number} className="py-2 text-xs">
                <div className="font-medium">{c.label}</div>
                <div className="text-ink-soft">No: {c.number} · CVV: {c.cvv} · Exp: {c.expiry} · OTP/3DS: {c.otp}</div>
              </div>
            ))}
          </div>
          <ul className="mt-3 space-y-1 text-xs text-lm-red list-disc pl-4">
            {cfg.donts.map((d) => <li key={d}>{d}</li>)}
          </ul>
        </section>
      </div>

      <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="sandbox-history">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold">Riwayat transaksi uji ({tx.count || 0})</h2>
          <div className="flex gap-2">
            <button data-testid="sandbox-refresh" onClick={loadTx}
              className="h-9 px-3 rounded-md border border-black/15 text-xs inline-flex items-center gap-1.5">
              <RefreshCw size={12} /> Muat ulang
            </button>
            <button data-testid="sandbox-cleanup" onClick={cleanup} disabled={busy === "cleanup"}
              className="h-9 px-3 rounded-md border border-lm-red/40 text-lm-red text-xs inline-flex items-center gap-1.5">
              <Trash2 size={12} /> Hapus data uji
            </button>
          </div>
        </div>
        <div className="mt-3 space-y-3">
          {(tx.transactions || []).length === 0 && (
            <div className="text-xs text-ink-soft" data-testid="sandbox-empty">Belum ada transaksi uji.</div>
          )}
          {(tx.transactions || []).map((t) => (
            <div key={t.booking_code} className="border border-black/10 rounded-md p-3" data-testid={`sandbox-tx-${t.booking_code}`}>
              <div className="flex justify-between gap-3 flex-wrap text-sm">
                <span className="font-semibold">{t.booking_code}</span>
                <span>{rp(t.total_payment)} · {t.payment_status} · {t.booking_status}</span>
              </div>
              <div className="text-[11px] text-ink-soft">
                {t.rental_date} {t.start_time} · {t.package} · {t.quantity} unit · deposit {rp(t.deposit_amount)}
              </div>
              <div className="mt-2 space-y-1">
                {t.attempts.map((a) => (
                  <div key={a.attempt_order_id} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="truncate">{a.attempt_order_id} · {a.status}{a.payment_type ? ` · ${a.payment_type}` : ""}
                      {a.confirmed_with_midtrans ? " · terverifikasi" : ""}</span>
                    <button data-testid={`sandbox-check-${a.attempt_order_id}`}
                      onClick={() => checkStatus(a.attempt_order_id)} disabled={busy === a.attempt_order_id}
                      className="shrink-0 px-2 py-1 rounded border border-black/15 hover:border-lm-red">
                      Cek status di Midtrans
                    </button>
                  </div>
                ))}
                {t.events.map((e) => (
                  <div key={`${e.id || e.received_at}-${e.kind}`} className="text-[10px] text-ink-soft">
                    {e.received_at?.slice(0, 16)} · {e.kind} · {e.payload?.transaction_status || "-"}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
